import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, HeartPulse, ShieldCheck, Users } from "lucide-react";

import { DisposicionSitio } from "@/components/medam/sitio";
import { Button } from "@/components/ui/button";
import { MISION_MEDAM, VALORES_MEDAM, VISION_MEDAM } from "@/lib/catalogos";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Forma parte de MEDAM | Construyamos juntos una nueva etapa" },
      {
        name: "description",
        content:
          "MEDAM está próximo a iniciar operaciones. Consulta las posiciones disponibles y envía tu postulación para formar parte del equipo.",
      },
      { property: "og:title", content: "Forma parte de MEDAM" },
      {
        property: "og:description",
        content:
          "Consulta las posiciones disponibles y cuéntanos por qué quieres formar parte de MEDAM.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Portada,
});

function Portada() {
  return (
    <DisposicionSitio>
      <section className="superficie-institucional border-b border-border/60">
        <div className="mx-auto grid w-full max-w-6xl gap-12 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div>
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-card px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-primary">
              Convocatoria abierta
            </p>
            <h1 className="font-display text-4xl leading-[1.08] text-foreground sm:text-5xl lg:text-6xl">
              Construyamos juntos una nueva etapa
            </h1>
            <div className="mt-6 max-w-2xl space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                MEDAM está próximo a iniciar operaciones y estamos conformando al equipo que dará
                vida a este nuevo proyecto de salud.
              </p>
              <p>
                Queremos comenzar este proceso invitando a personas cuya experiencia, conocimiento y
                compromiso pueden aportar valor desde el primer día.
              </p>
              <p>
                Consulta las posiciones disponibles y cuéntanos por qué quieres formar parte de
                MEDAM.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="h-12 px-6 text-base">
                <Link to="/posiciones">
                  Conocer las posiciones disponibles
                  <ArrowRight className="ml-1 size-5" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            {[
              {
                icono: HeartPulse,
                titulo: "Un proyecto que inicia",
                texto: "Formarás parte del equipo que define la manera de trabajar desde el día uno.",
              },
              {
                icono: ShieldCheck,
                titulo: "Estándares claros",
                texto: "Procesos definidos, seguridad del paciente y responsabilidad compartida.",
              },
              {
                icono: Users,
                titulo: "Proceso cuidadoso",
                texto: "Cada postulación se revisa conforme a los requisitos de la posición.",
              },
            ].map((item) => (
              <div
                key={item.titulo}
                className="rounded-2xl border border-border/70 bg-card/90 p-5 shadow-sm"
              >
                <item.icono className="size-5 text-primary" aria-hidden="true" />
                <h2 className="mt-3 font-display text-lg">{item.titulo}</h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.texto}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 pt-16 sm:px-6">
        <div className="grid gap-4 md:grid-cols-2">
          <article className="rounded-3xl border border-border/70 bg-card p-7">
            <h2 className="font-display text-xl text-primary">Misión</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{MISION_MEDAM}</p>
          </article>
          <article className="rounded-3xl border border-border/70 bg-primary/5 p-7">
            <h2 className="font-display text-xl text-primary">Visión</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{VISION_MEDAM}</p>
          </article>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6">
        <h2 className="font-display text-2xl sm:text-3xl">Nuestros valores</h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Estos principios guían la forma en la que atendemos, decidimos y trabajamos en MEDAM.
        </p>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {VALORES_MEDAM.map((valor) => (
            <li
              key={valor.titulo}
              className="rounded-2xl border border-border/70 bg-card p-6 transition-shadow hover:shadow-md"
            >
              <span className="block h-1 w-10 rounded-full bg-accent" aria-hidden="true" />
              <h3 className="mt-4 font-display text-lg">{valor.titulo}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {valor.descripcion}
              </p>
            </li>
          ))}
        </ul>
      </section>
    </DisposicionSitio>
  );
}
